import {
  RELEASED_AT,
  RELEASE_NOTES,
  RELEASE_VERSION,
  formatReleaseDate,
  type ReleaseNote,
} from "../releaseInfo";
import { CUSTOM_PANEL_MARKER_ATTR } from "./tabs";

const OVERLAY_ID = "sfe-release-notes-overlay";

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function isMaintenanceRelease(note: ReleaseNote): boolean {
  return note.summary.trim() === "Maintenance release.";
}

function visibleReleaseNotes(): ReleaseNote[] {
  return RELEASE_NOTES.filter((note) => !isMaintenanceRelease(note));
}

function findRelease(version: string): ReleaseNote | undefined {
  return visibleReleaseNotes().find((note) => note.version === version);
}

function formatSummaryHtml(summary: string): string {
  const lines = summary
    .split("\n")
    .map((line) => line.trim().replace(/^[-*]\s*/, ""))
    .filter(Boolean);
  if (lines.length <= 1) {
    return `<p class="sfe-release-note-summary">${esc(lines[0] ?? summary.trim())}</p>`;
  }
  return `<ul class="sfe-release-note-list">${lines.map((line) => `<li>${esc(line)}</li>`).join("")}</ul>`;
}

function renderReleaseListBodyHtml(): string {
  const notes = visibleReleaseNotes();
  if (!notes.length) {
    return `<p class="muted small sfe-release-notes-empty">No release history yet.</p>`;
  }
  return `<ul class="sfe-release-index">${notes.map(
    (note) => `
    <li class="sfe-release-index-item">
      <span class="sfe-release-index-label">
        <strong>v${esc(note.version)}</strong>
        <span class="sfe-release-index-date">${esc(formatReleaseDate(note.releasedAt))}</span>
      </span>
      <button
        type="button"
        class="sfe-release-notes-link"
        data-action="open-release-detail"
        data-release-version="${esc(note.version)}"
      >Release notes</button>
    </li>`,
  ).join("")}</ul>`;
}

function renderReleaseDetailBodyHtml(note: ReleaseNote): string {
  return `
    <article class="sfe-release-note sfe-release-note--detail">
      <p class="sfe-release-note-meta muted small">Released ${esc(formatReleaseDate(note.releasedAt))}</p>
      ${formatSummaryHtml(note.summary)}
    </article>`;
}

function renderShellHtml(bodyHtml: string, opts: { detail?: boolean; version?: string }): string {
  const title = opts.detail && opts.version ? `v${esc(opts.version)}` : "Release notes";
  const backBtn = opts.detail
    ? `<button type="button" class="sfe-modal-back sfe-release-notes-back" data-action="release-notes-back" aria-label="Back to release list">←</button>`
    : "";
  return `
    <div class="sfe-modal sfe-confirm-modal sfe-content-modal sfe-release-notes-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-release-notes-title">
      <div class="sfe-modal-header sfe-release-notes-header">
        ${backBtn}
        <h3 id="sfe-release-notes-title">${title}</h3>
        <button type="button" class="sfe-modal-close" data-action="release-notes-close" aria-label="Close">×</button>
      </div>
      <div class="sfe-content-dialog-body sfe-release-notes-body">${bodyHtml}</div>
      <div class="sfe-modal-actions">
        <button type="button" class="sfe-btn sfe-btn-primary" data-action="release-notes-close">Close</button>
      </div>
    </div>`;
}

function removeOverlay(): void {
  document.getElementById(OVERLAY_ID)?.remove();
}

function mountOverlay(bodyHtml: string, opts: { detail?: boolean; version?: string } = {}): HTMLElement {
  removeOverlay();
  const overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.className = "sfe-modal-overlay sfe-modal-open sfe-confirm-overlay";
  overlay.setAttribute(CUSTOM_PANEL_MARKER_ATTR, "true");
  overlay.setAttribute("data-sfe-injected", "true");
  overlay.innerHTML = renderShellHtml(bodyHtml, opts);
  document.body.appendChild(overlay);
  return overlay;
}

function showReleaseList(): void {
  const overlay = mountOverlay(renderReleaseListBodyHtml());
  bindOverlayActions(overlay);
}

function showReleaseDetail(version: string): void {
  const note = findRelease(version);
  if (!note) {
    showReleaseList();
    return;
  }
  const overlay = mountOverlay(renderReleaseDetailBodyHtml(note), {
    detail: true,
    version: note.version,
  });
  bindOverlayActions(overlay);
}

function closeReleaseNotesModal(): void {
  document.removeEventListener("keydown", onReleaseNotesKey, true);
  removeOverlay();
}

function onReleaseNotesKey(event: KeyboardEvent): void {
  if (event.key === "Escape") {
    event.preventDefault();
    closeReleaseNotesModal();
  }
}

function bindOverlayActions(overlay: HTMLElement): void {
  document.addEventListener("keydown", onReleaseNotesKey, true);

  overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (target === overlay) {
        closeReleaseNotesModal();
        return;
      }

      const actionEl = target.closest<HTMLElement>("[data-action]");
      const action = actionEl?.dataset.action;
      if (action === "release-notes-close") {
        closeReleaseNotesModal();
        return;
      }
      if (action === "release-notes-back") {
        showReleaseList();
        return;
      }
      if (action === "open-release-detail") {
        const version = actionEl?.dataset.releaseVersion;
        if (version) showReleaseDetail(version);
      }
    });
}

/** Version + release date + link — place below settings action buttons. */
export function releaseInfoSettingsHtml(): string {
  return `
    <div class="sfe-release-info">
      <p class="muted small sfe-release-info-line">Version ${esc(RELEASE_VERSION)} · Released ${esc(formatReleaseDate(RELEASED_AT))}</p>
      <p class="muted small sfe-release-info-line">
        <button type="button" class="sfe-release-notes-link" data-action="open-release-notes">Release notes</button>
      </p>
    </div>`;
}

export function openReleaseNotesModal(): void {
  showReleaseList();
}

export function bindReleaseNotesAction(root: HTMLElement): void {
  root.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.closest('[data-action="open-release-notes"]')) return;
    event.preventDefault();
    openReleaseNotesModal();
  });
}
